import React, { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { heartbeatSync } from "../../../../State/Slices/gameSettingsSlice";
import {
  selectAnyFeatureActive,
  selectWinAnimationsPlaying,
  selectCriticalAnimationPlaying,
  selectSceneTransitionActive,
  selectPotCollectionTransitionActive,
} from "../../../../State/selectors";
import { FEATURES_CONFIG } from "../../../../featuresConfig";

/**
 * HeartbeatManager - Manages periodic balance synchronization
 *
 * Features:
 * - Calls init API every 30 seconds to sync balance ONLY (heartbeatSync)
 * - Starts 30 seconds after game initialization
 * - Pauses during active spins, bonus games, win animations, and transitions
 * - Handles errors gracefully without blocking gameplay
 * - Tracks consecutive failures for error state management
 *
 * CRITICAL: Uses heartbeatSync (balance-only) instead of refreshGameData.
 * refreshGameData overwrites ALL game state (featureState, gameMode, currentRound)
 * and must only be used for error recovery and feature transitions.
 * 
 * Can be disabled via FEATURES_CONFIG.HEARTBEAT.ENABLED
 */
const HeartbeatManager = () => {
  const dispatch = useDispatch();

  // Selectors
  const gameStatus = useSelector((state) => state.game.status);
  const assetsStatus = useSelector((state) => state.assets.status);

  // Safety guards — heartbeat must NEVER fire during any of these
  const anyFeatureActive = useSelector(selectAnyFeatureActive);
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const sceneTransitionActive = useSelector(selectSceneTransitionActive);
  const potCollectionTransitionActive = useSelector(selectPotCollectionTransitionActive);

  // Refs to persist values across renders
  const intervalRef = useRef(null);
  const startDelayRef = useRef(null);
  const isRunningRef = useRef(false);

  // Check if game is ready to start heartbeat
  const isGameReady =
    assetsStatus === "succeeded" &&
    gameStatus === "succeeded";

  // Check if spin is in progress
  const isSpinning = gameStatus === "spinning";

  // Check if anything gameplay-related is happening
  const isBusy =
    isSpinning ||
    anyFeatureActive ||
    winAnimationsPlaying ||
    criticalAnimationPlaying ||
    sceneTransitionActive ||
    potCollectionTransitionActive;

  /**
   * Execute heartbeat sync
   * Skips if already running, spinning, in a bonus, or during win animations
   */
  const executeHeartbeat = useCallback(() => {
    // Skip if previous heartbeat still running
    if (isRunningRef.current) {
      return;
    }

    // Skip if anything gameplay-related is happening
    if (isBusy) {
      return;
    }

    // Execute heartbeat - balance-only sync, does NOT overwrite game state
    isRunningRef.current = true;
    dispatch(heartbeatSync())
      .catch(() => {
        // Fail silently
      })
      .finally(() => {
        isRunningRef.current = false;
      });
  }, [dispatch, isBusy]);

  /**
   * Start heartbeat interval
   */
  const startHeartbeat = useCallback(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Start interval
    intervalRef.current = setInterval(() => {
      executeHeartbeat();
    }, FEATURES_CONFIG.HEARTBEAT.INTERVAL);
  }, [executeHeartbeat]);

  /**
   * Stop heartbeat interval
   */
  const stopHeartbeat = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * Initialize heartbeat with delay
   */
  useEffect(() => {
    // Don't start heartbeat if disabled
    if (!FEATURES_CONFIG.HEARTBEAT.ENABLED) {
      return;
    }

    if (!isGameReady) {
      return;
    }

    // Start heartbeat after initial delay
    startDelayRef.current = setTimeout(() => {
      startHeartbeat();
    }, FEATURES_CONFIG.HEARTBEAT.START_DELAY);

    // Cleanup on unmount
    return () => {
      if (startDelayRef.current) {
        clearTimeout(startDelayRef.current);
      }
      stopHeartbeat();
    };
  }, [isGameReady, startHeartbeat, stopHeartbeat, assetsStatus, gameStatus]);

  // This component doesn't render anything
  return null;
};

export default React.memo(HeartbeatManager);
